import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import type { BlockManifest, EditorField, PropSchema } from '../../lib/cmsAdmin';
import type { BlockNode } from '../../cms/editor/document';
import { MediaPicker, MediaSlot } from './MediaPicker';
import { GlobalSectionPicker, SectionSlot } from './GlobalSectionPicker';
import { useCopy } from '../../copy/CopyProvider';
import { listCopyKeys } from '../../lib/copyAdmin';

/**
 * The block inspector.
 *
 * ── GENERATED, NEVER HAND-WRITTEN ────────────────────────────────────────────────────
 *
 * Every control below is derived from the block's own manifest: its prop schema says what
 * a value may be, and its editor hints say how to present it. Nothing here knows what an
 * EventHero is. That is the point — a block added to the registry gets an inspector with
 * no work, and a block whose props change cannot leave a stale form behind that writes
 * properties the server will refuse.
 *
 * ── A PROP IS NOT THE PLACE FOR PRODUCT WORDING ──────────────────────────────────────
 *
 * Blocks whose text comes from the Copy Layer declare those keys. The inspector shows
 * them as a link to where they are edited rather than offering a second box for the same
 * sentence — two editable copies of one string is how a product ends up saying different
 * things in different places.
 */
export function Inspector({
  node, manifest, organizationId, scope, onChange,
}: {
  node: BlockNode;
  manifest: BlockManifest | null;
  organizationId: string | null;
  scope: 'platform' | 'organization';
  onChange: (props: Record<string, unknown>) => void;
}) {
  const [picking, setPicking] = useState<{ prop: string; kind: 'image' | 'video' } | null>(null);
  const [pickingSection, setPickingSection] = useState<string | null>(null);

  const fields = useMemo(() => {
    const properties = manifest?.propsSchema?.properties ?? {};
    const hints = manifest?.editor?.fields ?? {};
    return Object.entries(properties).map(([name, schema]) => ({
      name, schema, hint: hints[name] ?? {},
    }));
  }, [manifest]);

  if (!manifest) {
    return (
      <div className="cms-inspector">
        <p className="alert alert--error" role="alert">
          &ldquo;{node.component}&rdquo; is not a block this version of Voleto knows, so
          there is nothing to edit. Remove it, or deploy the version that has it.
        </p>
      </div>
    );
  }

  const grouped = fields.reduce<Record<string, typeof fields>>((acc, field) => {
    const group = (field.hint as EditorField).group ?? 'Settings';
    (acc[group] ??= []).push(field);
    return acc;
  }, {});

  return (
    <div className="cms-inspector">
      <header className="cms-inspector__head">
        <h3>{manifest.label}</h3>
        <p className="cms-inspector__id">
          <code>{node.id}</code> · v{node.v}
        </p>
      </header>

      {manifest.editor?.note && <p className="adm-hint">{manifest.editor.note}</p>}

      {fields.length === 0 && (
        <p className="adm-muted">This block has no settings.</p>
      )}

      {Object.entries(grouped).map(([group, groupFields]) => (
        <section key={group} className="cms-inspector__group">
          <h4>{group}</h4>
          {groupFields.map(({ name, schema, hint }) => (
            <Field
              key={name}
              name={name}
              schema={schema}
              hint={hint as EditorField}
              value={node.props?.[name]}
              onChange={(value) => onChange({ [name]: value })}
              onPickMedia={(kind) => setPicking({ prop: name, kind })}
              onPickSection={() => setPickingSection(name)}
            />
          ))}
        </section>
      ))}

      {(manifest.editor?.copyKeys ?? []).length > 0 && (
        <CopyKeys keys={manifest.editor?.copyKeys ?? []} />
      )}

      {manifest.dataRequirements.length > 0 && (
        <p className="adm-hint cms-inspector__data">
          Reads {manifest.dataRequirements.join(', ')} from the event. That data is never
          part of the layout, so this block always shows the real event.
        </p>
      )}

      {pickingSection && (
        <GlobalSectionPicker
          value={(node.props?.[pickingSection] as string | null) ?? null}
          organizationId={organizationId}
          scope={scope}
          onChange={(sectionId) => onChange({ [pickingSection]: sectionId })}
          onClose={() => setPickingSection(null)}
        />
      )}

      {picking && (
        <MediaPicker
          value={(node.props?.[picking.prop] as string | null) ?? null}
          mediaKind={picking.kind}
          organizationId={organizationId}
          scope={scope}
          onChange={(assetId) => onChange({ [picking.prop]: assetId })}
          onClose={() => setPicking(null)}
        />
      )}
    </div>
  );
}

function Field({
  name, schema, hint, value, onChange, onPickMedia, onPickSection,
}: {
  name: string;
  schema: PropSchema;
  hint: EditorField;
  value: unknown;
  onChange: (value: unknown) => void;
  onPickMedia: (kind: 'image' | 'video') => void;
  onPickSection: () => void;
}) {
  const label = hint.label ?? name;
  const control = hint.control
    ?? (schema.globalSectionReference ? 'globalSection'
      : schema.mediaReference ? 'media'
        : schema.enum ? 'select'
          : schema.type === 'boolean' ? 'boolean'
            : schema.type === 'number' || schema.type === 'integer' ? 'number' : 'text');

  if (control === 'globalSection') {
    return (
      <div className="field">
        <span className="field__label">{label}</span>
        <SectionSlot
          sectionId={(value as string | null) ?? null}
          onOpen={onPickSection}
        />
        {hint.helpText && <span className="field__help">{hint.helpText}</span>}
      </div>
    );
  }

  // §63/§68: arrays of authored items — footer links, sponsor logos — have no editor in
  // v1. Saying so is better than rendering a text box that writes a string into a slot
  // the validator expects to be an array, which is a save the server refuses with a
  // message about types.
  if (control === 'links' || control === 'logos') {
    const count = Array.isArray(value) ? value.length : 0;
    return (
      <div className="field">
        <span className="field__label">{label}</span>
        <p className="adm-muted">
          {count === 0
            ? 'None yet.'
            : `${count} ${control === 'links' ? 'link' : 'logo'}${count === 1 ? '' : 's'}.`}
        </p>
        <span className="field__help">
          {hint.helpText ? `${hint.helpText} ` : ''}
          Editing these lists is not in this release.
        </span>
      </div>
    );
  }

  if (control === 'media') {
    const kind = hint.mediaKind ?? schema.mediaKind ?? 'image';
    return (
      <div className="field">
        <span className="field__label">{label}</span>
        <MediaSlot
          assetId={(value as string | null) ?? null}
          emptyNote={hint.helpText ?? 'Nothing chosen.'}
          onOpen={() => onPickMedia(kind)}
        />
        {hint.helpText && value != null && <span className="field__help">{hint.helpText}</span>}
      </div>
    );
  }

  if (control === 'boolean') {
    return (
      <label className="field field--check">
        <input type="checkbox" checked={value === true}
          onChange={(e) => onChange(e.target.checked)} />
        <span className="field__label">{label}</span>
        {hint.helpText && <span className="field__help">{hint.helpText}</span>}
      </label>
    );
  }

  if (control === 'select' || schema.enum) {
    return (
      <label className="field">
        <span className="field__label">{label}</span>
        <select className="field__input" value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}>
          {(schema.enum ?? []).map((option) => (
            <option key={String(option)} value={String(option)}>{String(option)}</option>
          ))}
        </select>
        {hint.helpText && <span className="field__help">{hint.helpText}</span>}
      </label>
    );
  }

  if (control === 'number') {
    return (
      <label className="field">
        <span className="field__label">{label}</span>
        <input className="field__input" type="number" value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
        {hint.helpText && <span className="field__help">{hint.helpText}</span>}
      </label>
    );
  }

  const Tag = control === 'textarea' ? 'textarea' : 'input';
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <Tag
        className="field__input"
        value={String(value ?? '')}
        maxLength={schema.maxLength}
        placeholder={hint.placeholder}
        onChange={(e: { target: { value: string } }) => onChange(e.target.value)}
      />
      {schema.maxLength && (
        <span className="field__help">
          {String(value ?? '').length} / {schema.maxLength}
        </span>
      )}
      {hint.helpText && <span className="field__help">{hint.helpText}</span>}
    </label>
  );
}

/** The shipped defaults, loaded once. useCopy needs one for every key it resolves. */
let defaultsCache: Map<string, string> | null = null;

function useCopyDefaults(): Map<string, string> {
  const [defaults, setDefaults] = useState<Map<string, string>>(defaultsCache ?? new Map());
  useEffect(() => {
    if (defaultsCache) return;
    let cancelled = false;
    listCopyKeys()
      .then((rows) => {
        if (cancelled) return;
        defaultsCache = new Map(rows.map((r) => [r.key, r.codeDefault]));
        setDefaults(defaultsCache);
      })
      .catch(() => { /* The link still works; only the preview text is unavailable. */ });
    return () => { cancelled = true; };
  }, []);
  return defaults;
}

/**
 * The words this block will show, and where they are changed.
 *
 * The text is resolved through the same CopyProvider the product uses, AT THIS CONSOLE'S
 * SCOPE — an organization, venue or event override can say something different on the
 * page itself. That is stated rather than hidden, because a preview that silently shows
 * the wrong scope's wording is worse than one that says which scope it is showing.
 */
function CopyKeys({ keys }: { keys: { key: string; label: string }[] }) {
  const copy = useCopy();
  const defaults = useCopyDefaults();
  return (
    <section className="cms-inspector__group cms-inspector__copy">
      <h4>Wording</h4>
      <p className="adm-hint">
        These words come from the Copy Layer, so they stay consistent everywhere the
        product says them. They are edited there, not here. What is shown is the wording
        at platform level; an organization, venue or event override can change it.
      </p>
      <ul className="cms-copykeys">
        {keys.map(({ key, label }) => (
          <li key={key}>
            <span className="cms-copykeys__label">{label}</span>
            <span className="cms-copykeys__value">
              {copy(key, defaults.get(key) ?? '') || <em className="adm-muted">not set</em>}
            </span>
            <Link className="cms-copykeys__link" to={`/admin/copy?key=${encodeURIComponent(key)}`}>
              Edit in Copy
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
